import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IEmailTemplate,
  IEmailTemplateType,
  ISaveEmailTemplateRequest,
  ITestEmailTemplateRequest,
} from "../../../@types/admin/AdminEmailTemplate";
import { emailTemplatesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { emailTemplateKey } from "../../queries/admin/emailTemplates";

interface ISaveEmailTemplateArgs {
  wardId: number;
  templateType: IEmailTemplateType;
  request: ISaveEmailTemplateRequest;
}

export function useSaveEmailTemplate() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IEmailTemplate,
    Error,
    ISaveEmailTemplateArgs
  >({
    mutationFn: async ({ wardId, templateType, request }) =>
      ensureOk(
        await emailTemplatesService.saveEmailTemplate(
          wardId,
          templateType,
          request
        )
      ),
    onSuccess: (_data, { wardId, templateType }) => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKey(wardId, templateType),
      });
    },
  });

  return { saveEmailTemplate: mutateAsync, isSavingTemplate: isPending };
}

/**
 * ⚠️ Sends a real email to whatever address it is given. There is no query
 * invalidation here and deliberately no auto-run: the only caller is the
 * confirm button inside `SendTestEmailModal`.
 */
export function useSendTestEmail() {
  const { mutateAsync, isPending } = useMutation<
    void,
    Error,
    ITestEmailTemplateRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await emailTemplatesService.sendTestEmail(request)),
  });

  return { sendTestEmail: mutateAsync, isSendingTestEmail: isPending };
}
